import {
  MixpanelEvent,
  MixpanelEventProperty,
  SELECTION_LIST_TALLER_ITEM_HEIGHT,
  SelectionsList,
  useMarketsContext,
  useMixpanel,
  type SelectionListItem,
  type SelectionListSortProperty,
  type SortState,
} from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { AvailabilityEnum } from './types';
import { isMarketSelectorItemSelectable, marketSelectorItemComparator } from './utils/items';

interface MarketSelectorSelectionsListProps {
  placeholder?: string;
}

export const MarketSelectorSelectionsList = ({ placeholder }: MarketSelectorSelectionsListProps) => {
  const [sort, setSort] = useState<SortState<SelectionListSortProperty> | undefined>({
    key: 'label',
    direction: '-',
  });
  const {
    selectionsSet,
    items,
    itemsMap,
    unselect,
    enrichments,
    selections,
    selectionListShowAll,
    setSelectionListShowAll,
    selectMarket,
    select,
    selectOnly,
    unselectAll,
    selectAll,
  } = useMarketSelectorContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const mixpanel = useMixpanel();

  const selectionListEnrichmentSpec = useMemo(
    () => enrichments.find(spec => spec.selectionList != null)?.selectionList,
    [enrichments]
  );

  const getSelectionListHeaderSuffix = useCallback(
    () => selectionListEnrichmentSpec?.selectionListHeaderSuffix?.(selections),
    [selectionListEnrichmentSpec, selections]
  );

  const selectionListComparator = useMemo(() => {
    if (sort == null) {
      return () => 0; // shouldnt happen, but need to catch it
    }

    const baseComparator =
      sort.key === 'label' ? marketSelectorItemComparator : selectionListEnrichmentSpec?.comparator;
    const comparator = (a: SelectionListItem, b: SelectionListItem) => {
      if (!baseComparator) {
        return 0;
      }

      return baseComparator(itemsMap.get(a.key), itemsMap.get(b.key), sort.direction);
    };
    return comparator;
  }, [sort, itemsMap, selectionListEnrichmentSpec?.comparator]);

  const enrichmentParentComparator = selectionListEnrichmentSpec?.parentComparator;
  const selectionListParentComparator = useMemo(() => {
    if (sort == null) {
      return () => 0; // shouldnt happen, but need to catch it
    }

    const labelComparator = (a: SelectionListItem, b: SelectionListItem) => {
      if (sort.direction === '+') {
        // Instead of reversing the resulting array, flip the pointers
        const temp = a;
        a = b;
        b = temp;
      }

      return a.label.localeCompare(b.label);
    };

    const suffixComparator = (a: SelectionListItem, b: SelectionListItem) => {
      return enrichmentParentComparator ? enrichmentParentComparator(a.key, b.key, sort.direction) : 0;
    };

    return sort.key === 'suffix' ? suffixComparator : labelComparator;
  }, [sort, enrichmentParentComparator]);

  const selectionListItemSuffix = selectionListEnrichmentSpec?.selectionListItemSuffix;
  const selectionListItems = useMemo(() => {
    return items
      .filter(item => selectionsSet.has(item.name) || isMarketSelectorItemSelectable(item))
      .map(
        item =>
          ({
            selected: selectionsSet.has(item.name),
            disabled: selectionsSet.has(item.name) ? false : !isMarketSelectorItemSelectable(item),
            key: item.name,
            label: item.displayName,
            parentKey: item.market.Name,

            warning: item.availability.availability < AvailabilityEnum.Ok ? item.availability?.render?.() : undefined,
            suffix: selectionListItemSuffix?.(item.name, selectionsSet.has(item.name)),
          }) satisfies SelectionListItem
      );
  }, [items, selectionsSet, selectionListItemSuffix]);

  const getParentLabel = useCallback(
    (marketName: string) => {
      return marketDisplayNameByName.get(marketName) ?? marketName;
    },
    [marketDisplayNameByName]
  );

  const handleDelete = useCallback(
    (selection: string) => {
      unselect(selection);
      mixpanel.track(MixpanelEvent.UnselectMarketAccount);
    },
    [unselect, mixpanel]
  );

  const handleOnly = useCallback(
    (key: string) => {
      mixpanel.track(MixpanelEvent.UpdateMarketSelection, {
        [MixpanelEventProperty.Method]: 'Only',
      });
      selectOnly(key);
    },
    [mixpanel, selectOnly]
  );

  const handleShowAllChange = useCallback(
    (value: boolean) => {
      mixpanel.track(value ? MixpanelEvent.ClickShowAllMarketAccounts : MixpanelEvent.ClickShowSelectedMarketAccounts);
      setSelectionListShowAll(value);
    },
    [mixpanel, setSelectionListShowAll]
  );

  return (
    <SelectionsList
      items={selectionListItems}
      placeholder={placeholder}
      onDelete={handleDelete}
      getHeaderSuffix={getSelectionListHeaderSuffix}
      getParentLabel={getParentLabel}
      itemHeight={
        selectionListEnrichmentSpec?.selectionListItemHeight === 'taller'
          ? SELECTION_LIST_TALLER_ITEM_HEIGHT
          : undefined
      }
      sort={sort}
      sortComparator={selectionListComparator}
      parentSortComparator={selectionListParentComparator}
      onSortChange={setSort}
      onOnlyClicked={handleOnly}
      onDeleteAll={unselectAll}
      onSelectAll={selectAll}
      showAll={selectionListShowAll}
      onShowAllChange={handleShowAllChange}
      onSelect={select}
      onSelectParent={selectMarket}
    />
  );
};
